import {
  AccountTypeEnum,
  OrganizationApplicationStatusEnum,
  ProfessionalApplicationStatusEnum,
} from '@platform/client/schemas';
import { FormsInputDropdown, InputDropdownItem } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminFormModal, ModalStage } from '../AdminFormModal';
import { useState } from 'react';
import { AdminProfessionals, AdminRepresentatives, AdminUsers } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { AdminTableContext } from '../AdminTable';

type ResetUserModalFormData = {
  userType: AccountTypeEnum;
};

const accountTypeItems: InputDropdownItem[] = [
  { text: 'Experteer', textValue: AccountTypeEnum.experteer },
  { text: 'Organization', textValue: AccountTypeEnum.organization },
];

export const ResetUserModal = (props: {
  userId: number;
  currentAccountType: AccountTypeEnum;
  currentAppStatus: ProfessionalApplicationStatusEnum | OrganizationApplicationStatusEnum | null;
  onClose: () => void;
}) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { handleSubmit, control } = useForm<ResetUserModalFormData>({
    values: {
      userType: props.currentAccountType,
    },
  });
  const queryClient = useQueryClient();
  const resetUserMutator = AdminUsers.useResetUserType({
    mutation: {
      onMutate() {
        setModalStage('submitting');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminProfessionals.getGetAdminProfessionalsQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminRepresentatives.getGetRepresentativesQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<ResetUserModalFormData> = (data) => {
    resetUserMutator.mutate({
      data: {
        userId: props.userId,
        newUserType: data.userType,
      },
    });
  };

  return (
    <AdminFormModal
      title='Reset User'
      modalStage={modalStage}
      onClose={props.onClose}
      submitCTA={{
        text: 'Reset user',
        callback: () => handleSubmit(onSubmit)(),
      }}
    >
      <div className='flex flex-col'>
        <p className='mb-2'>
          <span className='font-semibold'>Current account type:</span> {props.currentAccountType}
        </p>
        <p className='mb-4'>
          <span className='font-semibold'>Current application status:</span>{' '}
          {props.currentAppStatus}
        </p>
        <form>
          <FormsInputDropdown
            label='New user type'
            control={control}
            name='userType'
            type='default'
            items={accountTypeItems}
          />
        </form>
      </div>
    </AdminFormModal>
  );
};
